/**
 * Application entry point.
 *
 * Sets up:
 * 1. A pre-router OIDC redirect resolution (`resolveAuthRedirects`, called
 *    from `bootstrap`). brewauth redirects back to real paths (`/auth/callback`,
 *    and `/?state=` after end_session), not hash routes, so they MUST be
 *    handled before the HashRouter app tree mounts — the router can't see
 *    them.
 * 2. React 18 root rendering
 * 3. HashRouter for backwards compatibility with the legacy AngularJS hash-based routing
 *    (existing bookmarks like #/dashboard/build will continue to work)
 * 4. AuthProvider for global authentication state
 * 5. App component with all route definitions
 *
 * Tailwind CSS is imported via index.css.
 */
import '@/utils/monacoTheme';
import '@/utils/preloadErrorReload';
import { createRoot } from 'react-dom/client';
import { HashRouter } from 'react-router';
import { AppInfoProvider } from '@/app/AppInfoContext';
import { AuthProvider } from '@/auth/AuthContext';
import ReauthModalHost from '@/auth/ReauthModalHost';
import { resolveAuthRedirects } from '@/auth/authRedirects';
import App from './App';
import './index.css';
import './styles/shared.css';

async function bootstrap() {
  // The re-auth popup's window and the re-auth modal's sign-in frame never
  // mount the app (see resolveAuthRedirects).
  if ((await resolveAuthRedirects()) !== 'app') return;

  createRoot(document.getElementById('root')!).render(
    <HashRouter>
      <AppInfoProvider>
        <AuthProvider>
          <App />
          <ReauthModalHost />
        </AuthProvider>
      </AppInfoProvider>
    </HashRouter>
  );
}

// fire-and-forget: resolveAuthRedirects() catches its OIDC callback failures
void bootstrap(); // NOSONAR typescript:S7785 refactor-deferred: entry-point bootstrap is deliberately fire-and-forget; top-level await would hold module evaluation on resolveAuthRedirects()
